// Golden locator rings for the dock firefly tab — three amber halos that
// collapse inward (dev-sync e8745c02). V10 moved the affordance from
// ActivityDockTab to ActivityDockFireflyTab; this hook owns the timing.

import { useCallback, useEffect, useRef, useState } from "react";

/** Session-scoped — once per browser tab open. */
export const ACTIVITY_DOCK_HALO_SESSION_KEY = "activityDockTab:haloShown";

/** Longest ring delay (400ms) + animation (1.5s) + buffer. */
export const ACTIVITY_DOCK_HALO_DURATION_MS = 2200;

export function sessionHaloAlreadyShown(
  storage: Pick<Storage, "getItem">,
): boolean {
  return storage.getItem(ACTIVITY_DOCK_HALO_SESSION_KEY) === "1";
}

export function markSessionHaloShown(storage: Pick<Storage, "setItem">): void {
  storage.setItem(ACTIVITY_DOCK_HALO_SESSION_KEY, "1");
}

export function useActivityDockGoldenHalo() {
  const [showHalo, setShowHalo] = useState(false);
  const timerRef = useRef<number | undefined>(undefined);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== undefined) {
      window.clearTimeout(timerRef.current);
      timerRef.current = undefined;
    }
  }, []);

  const playHalo = useCallback(() => {
    clearTimer();
    setShowHalo(true);
    timerRef.current = window.setTimeout(() => {
      setShowHalo(false);
      timerRef.current = undefined;
    }, ACTIVITY_DOCK_HALO_DURATION_MS);
  }, [clearTimer]);

  /** First app open per session — sets sessionStorage gate. */
  const playSessionHaloOnce = useCallback(() => {
    if (typeof window === "undefined") return;
    try {
      if (sessionHaloAlreadyShown(window.sessionStorage)) return;
      markSessionHaloShown(window.sessionStorage);
    } catch {
      // Private mode — still play once this mount.
    }
    playHalo();
  }, [playHalo]);

  useEffect(() => clearTimer, [clearTimer]);

  return { showHalo, playHalo, playSessionHaloOnce };
}
